// src/components/StudentManagement.js
import React, { useState } from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemAvatar, Avatar,
  ListItemText, IconButton, Divider, Alert, Dialog as MuiDialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import EditStudentDialog from './EditStudentDialog'; // 导入编辑对话框

// 从 props 接收 students, onStudentDelete, onStudentEdit
function StudentManagement({ students, onStudentDelete, onStudentEdit }) {
  // 删除对话框状态
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null); // 存储完整的学生对象

  // 编辑对话框状态
  const [editingStudent, setEditingStudent] = useState(null); // 存储完整的学生对象

  // 点击删除按钮
  const handleDeleteClick = (student) => {
    setStudentToDelete(student); // 存储整个学生对象
    setOpenDeleteDialog(true);
  };

  // 关闭删除对话框
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setStudentToDelete(null);
  };

  // 确认删除
  const handleConfirmDelete = () => {
    if (studentToDelete) {
      //传递 studentId (学号) 给 App.js 的处理函数 ---
      onStudentDelete(studentToDelete.studentId);
    }
    handleCloseDeleteDialog();
  };

  // 点击编辑按钮
  const handleEditClick = (student) => {
    console.log("编辑的学生数据:", student);
    setEditingStudent(student); // 设置要编辑的学生对象
  };

  // 关闭编辑对话框
  const handleCloseEditDialog = () => {
    setEditingStudent(null);
  };

  // 处理编辑保存 (从 EditStudentDialog 调用)
  // updatedStudentData 包含了 id(即studentId), name, studentId, faceImageUrl(新Base64或旧URL)
  const handleSaveChanges = async (updatedStudentData) => {
    console.log("StudentManagement: 准备保存更改:", updatedStudentData);
    // 调用 App.js 的 onStudentEdit，它会处理 API 调用
    // onStudentEdit 现在可能返回 true/false 指示 API 调用是否成功
    const success = await onStudentEdit(updatedStudentData);
    if (success) {
      handleCloseEditDialog(); // 仅在 API 调用成功后关闭对话框
    }
    // 如果失败，App.js 会显示错误，对话框保持打开让用户可以重试或取消
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        学生数据管理
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        这里显示已录入的学生信息。您可以查看、编辑或删除学生数据。
        头像是从服务器加载的。
      </Alert>

      {students.length === 0 ? (
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 3 }}>
          当前没有学生数据。请前往“学生信息录入”标签页添加。
        </Typography>
      ) : (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {/* 使用 student.id (也就是 studentId) 作为 key */}
          {students.map((student, index) => (
            <React.Fragment key={student.id}>
              <ListItem
                alignItems="flex-start"
                secondaryAction={
                  <Box>
                    <IconButton
                        edge="end" aria-label="edit" sx={{ mr: 1 }}
                        onClick={() => handleEditClick(student)} // 点击编辑
                        title="编辑"
                     >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                        edge="end" aria-label="delete"
                        onClick={() => handleDeleteClick(student)} // 点击删除
                        title="删除" color="error"
                    ><DeleteIcon /></IconButton>
                  </Box>
                }
              >
                <ListItemAvatar>
                   {/* Avatar 的 src 现在是来自服务器的 URL */}
                   <Avatar alt={student.name} src={student.faceImageUrl || undefined} /*onError={(e) => e.target.style.display='none'}*/>
                     {/* 如果 faceImageUrl 无效或不存在，显示 PersonIcon */}
                     {!student.faceImageUrl && <PersonIcon />}
                   </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`姓名: ${student.name}`}
                  // 使用 studentId 显示学号
                  secondary={`学号: ${student.studentId}`}
                />
              </ListItem>
              {index < students.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}

       {/* 删除确认对话框 */}
       <MuiDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
         <DialogTitle>确认删除</DialogTitle>
         <DialogContent>
           <DialogContentText>
             {/* 确认信息中使用 studentName 和 studentId */}
             确定要删除学生 "{studentToDelete?.name}" (学号: {studentToDelete?.studentId}) 的信息吗？此操作不可恢复。
           </DialogContentText>
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseDeleteDialog}>取消</Button>
           <Button onClick={handleConfirmDelete} color="error" autoFocus>
             确认删除
           </Button>
         </DialogActions>
       </MuiDialog>

       {/* 编辑对话框 */}
       {/* 只有当 editingStudent 不为 null 时才渲染 */}
       {editingStudent && (
         <EditStudentDialog
           open={!!editingStudent} // 如果 editingStudent 有值，则 open 为 true
           studentData={editingStudent} // 传递要编辑的学生数据
           onSave={handleSaveChanges} // 传递保存处理函数
           onClose={handleCloseEditDialog} // 传递关闭处理函数
         />
       )}
    </Paper>
  );
}

export default StudentManagement;